/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 8 de noviembre 2025
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import { useEffect, useState, useRef } from 'react';

/**
 * Hook para obtener la ubicación actual del usuario en tiempo real
 * Usa watchPosition de la Geolocation API
 *
 * @param {boolean} enabled - Si debe rastrear la ubicación o no
 * @param {object} options - Opciones para watchPosition
 * @returns {object} - { location, error, loading }
 */
const useCurrentLocation = (enabled = true, options = {}) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const watchIdRef = useRef(null);

  const defaultOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 5000,
    ...options,
  };

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    if (!navigator.geolocation) {
      setError('Geolocalización no soportada en este navegador');
      setLoading(false);
      return;
    }

    console.log('🌍 Iniciando seguimiento de ubicación en tiempo real...');

    // Callback de éxito
    const handleSuccess = (position) => {
      const newLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude,
        altitudeAccuracy: position.coords.altitudeAccuracy,
        heading: position.coords.heading,
        speed: position.coords.speed,
        timestamp: position.timestamp,
      };

      console.log('📍 Ubicación actualizada:', {
        lat: newLocation.latitude.toFixed(6),
        lng: newLocation.longitude.toFixed(6),
        accuracy: `${newLocation.accuracy.toFixed(2)}m`,
      });

      setLocation(newLocation);
      setError(null);
      setLoading(false);
    };

    // Callback de error
    const handleError = (err) => {
      let errorMessage = 'Error desconocido';

      switch (err.code) {
        case err.PERMISSION_DENIED:
          errorMessage = 'Permiso de ubicación denegado';
          break;
        case err.POSITION_UNAVAILABLE:
          errorMessage = 'Información de ubicación no disponible';
          break;
        case err.TIMEOUT:
          errorMessage = 'Timeout al obtener ubicación';
          break;
        default:
          errorMessage = err.message;
      }

      console.error('❌ Error de geolocalización:', errorMessage);
      setError(errorMessage);
      setLoading(false);
    };

    // Iniciar watchPosition
    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      defaultOptions
    );

    // Cleanup: detener watchPosition al desmontar
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
        console.log('🛑 Seguimiento de ubicación detenido');
      }
    };
  }, [enabled, defaultOptions.enableHighAccuracy, defaultOptions.timeout, defaultOptions.maximumAge]);

  return {
    location,
    error,
    loading,
  };
};

export default useCurrentLocation;
